import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, CheckCircle2, Download, FileSpreadsheet, Upload, XCircle } from "lucide-react";

import api, { API_BASE, formatApiErrorDetail, getAuthToken } from "@/lib/api";

export default function ImportEmployees() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onConflict, setOnConflict] = useState("update");
  const [createCampuses, setCreateCampuses] = useState(true);
  const [committed, setCommitted] = useState(null);

  const validRows = useMemo(
    () => (preview?.rows || []).filter((r) => !r.errors.length),
    [preview],
  );
  const errorRows = useMemo(
    () => (preview?.rows || []).filter((r) => r.errors.length),
    [preview],
  );

  const downloadTemplate = async (fmt) => {
    try {
      const url = `${API_BASE}/user-import/template.${fmt}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const blob = await resp.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `mypatria-employee-import-template.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dl);
    } catch (err) {
      toast.error("Download failed.");
    }
  };

  const uploadForPreview = async () => {
    if (!file) return;
    setBusy(true);
    setCommitted(null);
    setPreview(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/user-import/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.ok) {
        toast.error(data.error || "Preview failed.");
        return;
      }
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await api.post("/user-import/commit", {
        preview,
        on_conflict: onConflict,
        create_missing_campuses: createCampuses,
      });
      setCommitted(data);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadResults = () => {
    if (!committed) return;
    const headers = ["row", "email", "outcome", "user_id", "errors"];
    const csv = [
      headers.join(","),
      ...committed.results.map((r) => [
        r.row,
        r.email || "",
        r.outcome,
        r.user_id || "",
        `"${(r.errors || []).join("; ").replace(/"/g, '""')}"`,
      ].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mypatria-import-results-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadCredentials = () => {
    if (!committed || !committed.credentials.length) return;
    const csv = [
      "name,email,temporary_password",
      ...committed.credentials.map(
        (c) => `"${c.name.replace(/"/g, '""')}",${c.email},${c.temporary_password}`,
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mypatria-import-credentials-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="import-employees-page">
      <Link
        to="/people/manage"
        className="inline-flex items-center text-forest-700 text-sm font-medium"
      >
        <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
        Back To People Management
      </Link>

      <div>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          People Operations
        </p>
        <h1 className="text-4xl font-serif text-forest-900 mt-1">Import Employees</h1>
        <p className="mt-2 text-charcoal-soft max-w-2xl">
          Upload a CSV or Excel file to add or update teammates in bulk. Nothing is written
          until you review the preview and confirm.
        </p>
      </div>

      <section className="patria-card p-6" data-testid="import-template-panel">
        <h3 className="text-2xl font-serif text-forest-900">Start With A Template</h3>
        <p className="mt-2 text-sm text-charcoal-soft">
          Columns: name, work email, campus, department, job title, manager email,
          hire date, employment status, role. Headers are flexible — common variants like
          &quot;Title&quot;, &quot;Dept&quot;, or &quot;Manager Name&quot; are recognised.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => downloadTemplate("csv")}
            className="patria-btn-secondary"
            data-testid="btn-tpl-csv"
          >
            <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            Download CSV Template
          </button>
          <button
            onClick={() => downloadTemplate("xlsx")}
            className="patria-btn-secondary"
            data-testid="btn-tpl-xlsx"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            Download XLSX Template
          </button>
        </div>
      </section>

      <section className="patria-card p-6" data-testid="import-upload-panel">
        <h3 className="text-2xl font-serif text-forest-900">Upload Your File</h3>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm"
            data-testid="import-file-input"
          />
          <button
            onClick={uploadForPreview}
            disabled={!file || busy}
            className="patria-btn-primary"
            data-testid="btn-preview"
          >
            <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            {busy && !preview ? "Analysing…" : "Preview Import"}
          </button>
        </div>
      </section>

      {preview && !committed && (
        <>
          <section className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="preview-stats">
            <StatCard label="Total Rows" value={preview.totals.total} />
            <StatCard label="To Create" value={preview.totals.to_create} tone="ok" />
            <StatCard label="To Update" value={preview.totals.to_update} tone="ok" />
            <StatCard
              label="Errors"
              value={preview.totals.errored}
              tone={preview.totals.errored > 0 ? "warn" : "muted"}
            />
          </section>

          {preview.totals.new_campuses.length > 0 && (
            <div className="patria-card p-4 border-l-4 border-terracotta-500 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-terracotta-500 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-forest-900 font-medium">Campuses Will Be Created</p>
                <p className="text-sm text-charcoal-soft mt-1">
                  These campuses aren&apos;t in the system yet:{" "}
                  <b className="text-forest-900">
                    {preview.totals.new_campuses.join(", ")}
                  </b>
                </p>
              </div>
            </div>
          )}

          <section className="patria-card p-6 space-y-4" data-testid="import-commit-controls">
            <h3 className="text-2xl font-serif text-forest-900">Commit Options</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="patria-label">On Existing Match</label>
                <select
                  className="patria-input mt-2 w-full"
                  value={onConflict}
                  onChange={(e) => setOnConflict(e.target.value)}
                  data-testid="import-on-conflict"
                >
                  <option value="update">Update existing user</option>
                  <option value="skip">Skip existing user</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input
                  type="checkbox"
                  checked={createCampuses}
                  onChange={(e) => setCreateCampuses(e.target.checked)}
                  data-testid="import-create-campuses"
                />
                Create missing campuses on commit
              </label>
            </div>
            <div className="flex justify-end">
              <button
                onClick={commit}
                disabled={busy || validRows.length === 0}
                className="patria-btn-primary"
                data-testid="btn-commit"
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                {busy ? "Committing…" : `Commit ${validRows.length} Row${validRows.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </section>

          <section className="patria-card overflow-hidden" data-testid="preview-table">
            <table className="w-full text-sm">
              <thead className="bg-forest-600 text-cream-100">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Row</th>
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Email</th>
                  <th className="text-left px-3 py-2 font-medium">Role</th>
                  <th className="text-left px-3 py-2 font-medium">Manager</th>
                  <th className="text-left px-3 py-2 font-medium">Action</th>
                  <th className="text-left px-3 py-2 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sage-300/60">
                {preview.rows.map((r) => (
                  <tr
                    key={r.row_num}
                    data-testid={`preview-row-${r.row_num}`}
                    className={r.errors.length ? "bg-terracotta-500/5" : ""}
                  >
                    <td className="px-3 py-2 text-xs">{r.row_num}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                    <td className="px-3 py-2">{r.role}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.manager_resolution ? (
                        <>
                          <span className="text-charcoal-muted">[{r.manager_resolution.kind}]</span>{" "}
                          {r.manager_resolution.email}
                        </>
                      ) : (
                        <span className="text-charcoal-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ${
                          r.errors.length
                            ? "bg-terracotta-500 text-cream-100"
                            : r.action === "update"
                            ? "bg-terracotta-500/15 text-terracotta-600 border border-terracotta-500/30"
                            : "bg-forest-600 text-cream-100"
                        }`}
                      >
                        {r.errors.length ? "Error" : r.action === "update" ? "Update" : "Create"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.errors.length > 0 && (
                        <ul className="text-terracotta-600 space-y-0.5">
                          {r.errors.map((e, i) => (
                            <li key={`err-${e}-${i}`} className="flex items-start gap-1">
                              <XCircle className="h-3 w-3 mt-0.5 shrink-0" strokeWidth={1.5} />
                              {e}
                            </li>
                          ))}
                        </ul>
                      )}
                      {r.warnings.length > 0 && (
                        <ul className="text-terracotta-500 space-y-0.5 mt-1">
                          {r.warnings.map((w, i) => (
                            <li key={`warn-${w}-${i}`}>{w}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
                {preview.rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-charcoal-muted italic">
                      No rows parsed.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        </>
      )}

      {committed && (
        <section className="patria-card p-6 bg-forest-600 text-cream-100" data-testid="commit-result">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-6 w-6" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="font-serif text-2xl">Import Complete.</p>
              <p className="text-sm mt-1 text-cream-100/85">
                Created {committed.created} • Updated {committed.updated} • Skipped {committed.skipped} • Errored {committed.errored}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={downloadResults}
                  className="patria-btn-secondary text-cream-100 border-cream-100"
                  data-testid="btn-download-results"
                >
                  <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                  Download Row-Level Results
                </button>
                {committed.credentials.length > 0 && (
                  <button
                    onClick={downloadCredentials}
                    className="patria-btn-secondary text-cream-100 border-cream-100"
                    data-testid="btn-download-credentials"
                  >
                    <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                    Download Credentials (Shown Once)
                  </button>
                )}
              </div>
              {committed.credentials.length > 0 && (
                <p className="text-xs mt-3 text-cream-100/70 italic">
                  Save the credentials list now — temporary passwords are not shown again.
                </p>
              )}
              <div className="mt-4">
                <Link
                  to="/people/manage"
                  className="patria-btn-primary bg-cream-100 text-forest-900 hover:bg-cream-100/90"
                >
                  Back To People Management
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const cls =
    tone === "warn"
      ? "border-l-4 border-terracotta-500"
      : tone === "ok"
      ? "border-l-4 border-forest-600"
      : "";
  return (
    <div className={`patria-card p-4 ${cls}`}>
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{label}</p>
      <p className="font-serif text-forest-900 text-3xl mt-1">{value}</p>
    </div>
  );
}
